import { createContext, ReactNode, useMemo } from 'react';

export type ProductContextType = {
  /** Product name */
  name?: string;
};

export const ProductContext = createContext<ProductContextType>({});

/**
 * Lightweight provider component for product details.
 *
 * @param props - Component props
 * @param props.name - The name of the product that will be available through the context
 * @param props.children - React children to be wrapped by the provider
 * @returns Provider component with context value
 *
 * @example
 * ```tsx
 * function Page() {
 *   return (
 *     <ProductContextProvider name={product.name}>
 *       <YourComponent />
 *     </ProductContextProvider>
 *   );
 * }
 * ```
 *
 * @see useProductContext - Hook to access product context details
 */
export function ProductContextProvider({ name, children }: ProductContextType & { children: ReactNode }) {
  const value = useMemo(
    () => ({
      name,
    }),
    [name]
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}
